import type { ReactNode } from "react";

export type PaneCollapseStrategy = "drill" | "bottomSheet" | "stack";
export type PaneMaxWidth = "xsmall" | "small" | "medium" | "large" | "fit";

export interface FramePaneProps {
  children: ReactNode;
  /**
   * How the pane behaves when the viewport is too narrow to show it next to Content.
   * - `drill` (default): pane hides; consumer is expected to provide a separate index
   *   page that drills into details. Best for navigation panes (e.g. settings list).
   * - `bottomSheet`: pane hides and Frame surfaces a trigger button in the TopBar
   *   that opens the pane as a sheet. Best for filter/option panes.
   * - `stack`: pane stacks vertically above Content sharing the page's scroll. Best
   *   for metadata/supplementary panes.
   */
  collapse?: PaneCollapseStrategy;
  /**
   * Optional manual override of the pane's collapsed state. When provided, Frame
   * obeys it; when omitted, Frame infers from the viewport range and `collapse`.
   */
  collapsed?: boolean;
  /** Width cap when the pane is visible. Primer-style ladder, plus `fit` to size to
   *  the pane's own content. Default `small`. */
  maxWidth?: PaneMaxWidth;
  /** Apply standard interior padding (p-5 mobile, md:p-8). With `box-content` so the
   *  `maxWidth` size refers to the content area; padding sits outside it on the panel. */
  padded?: boolean;
}

/**
 * Side pane. `Frame.LeftPane` and `Frame.RightPane` are the same primitive
 * with different positioning — Frame decides where to render based on which
 * slot the consumer used.
 */
export function FrameLeftPane({ children }: FramePaneProps) {
  return <>{children}</>;
}

export function FrameRightPane({ children }: FramePaneProps) {
  return <>{children}</>;
}
